import { mainIconSize } from '@/constants';
import { Study } from '@/types';
import { type ButtonProps, getStackClassName } from '@nautilusbio/nb-design-system';
import { Breadcrumbs } from '@nautilusbio/nb-design-system/Breadcrumbs';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text, TextProps } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { Download, PencilIcon, Share2Icon } from 'lucide-react';
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditStudyForm } from './EditStudyForm';

interface StudyTitleBarProps {
  studyStyles: { [key: string]: string };
  pageStyles: { [key: string]: string };
  t: TFunction<'studies', undefined>;
  data?: Study;
  orgId?: string;
  canEdit?: boolean;
  detailTitle?: string;
}

const sharedBtnProps: Partial<ButtonProps> = {
  className: getStackClassName(['displayflex', 'horizontal', 'gap3']),
  size: 'md',
  visual: 'unstyled',
};
const sharedTextProps: Partial<TextProps> = {
  as: 'span',
  noWrap: true,
  size: 'md',
};

export const StudyTitleBar: FC<StudyTitleBarProps> = ({
  canEdit = false,
  data,
  detailTitle,
  orgId,
  pageStyles,
  studyStyles,
  t,
}) => {
  const { t: tStudy } = useTranslation('studies');
  const [editOpen, setEditOpen] = useState(false);

  const handleEditSuccess = useCallback(() => {
    setEditOpen(false);
  }, []);

  return (
    <Stack
      className={clsx(pageStyles.titleRow, pageStyles.organizationTitle, studyStyles.studyTitleBar)}
      fullWidth
      justifyContent="space-between"
      orientation="horizontal"
    >
      <Breadcrumbs
        links={[
          { id: 'studies', children: t('study.home'), to: '/studies' },
          {
            id: 'study',
            children: data?.name ?? '',
            ...(detailTitle ? { to: '/studies/$studyId', params: { studyId: data?.id ?? '' } } : {}),
          },
          ...(detailTitle ? [{ id: 'detail', children: detailTitle }] : []),
        ]}
        renderLink={(link, _index, defaultLinkProps) => {
          if (!link?.to) return link?.children;

          return (
            <Link className={defaultLinkProps.className} to={link.to}>
              {link.children}
            </Link>
          );
        }}
        separator="arrow"
        size="md"
      />

      <Grid columns={3} gap={4} justifyContent="end">
        <Dialog
          content={data && orgId ? <EditStudyForm orgId={orgId} study={data} onSuccess={handleEditSuccess} /> : null}
          data-testid="edit-study-btn"
          floatingProps={{ open: editOpen, onOpenChange: setEditOpen }}
          onOpenChange={setEditOpen}
          title={t('study.edit_study_title')}
          triggerProps={{
            children: (
              <>
                <PencilIcon size={mainIconSize} />
                <Text {...sharedTextProps}>{t('study.edit')}</Text>
              </>
            ),
            isDisabled: !canEdit,
            ...sharedBtnProps,
          }}
        />

        <Tooltip
          content={tStudy('study.share_coming_soon')}
          placement="bottom"
          trigger={
            <Button data-testid="share-study-btn" isDisabled {...sharedBtnProps}>
              <Share2Icon size={mainIconSize} />
              <Text {...sharedTextProps}>{t('study.share')}</Text>
            </Button>
          }
        />
        <Tooltip
          content={tStudy('study.download_coming_soon')}
          placement="bottom"
          trigger={
            <Button data-testid="download-study-btn" isDisabled {...sharedBtnProps}>
              <Download size={mainIconSize} />
              <Text {...sharedTextProps}>{t('study.download')}</Text>
            </Button>
          }
        />
      </Grid>
    </Stack>
  );
};
